import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  IconButton,
  Switch,
  FormControlLabel,
  InputLabel,
  Input,
  Alert,
  MenuItem,
} from '@mui/material';
import { Status, Trigger } from './types';
import TriggerForm from './TriggerForm.tsx';
import SubstatusList from './SubStatusList.tsx';
import CloseIcon from '@mui/icons-material/Close';
import CommonBadges from '../../Shared/Badges/index.jsx';
import { useParams } from 'react-router';

type StatusFormProps = {
  status: Status;
  onChange: (updatedStatus: Status) => void;
  onRemove: () => void;
  sNo?: any;
  validationError?: any;
};

function isLightColor(hex: string): boolean {
  const r = parseInt(hex.substr(1, 2), 16);
  const g = parseInt(hex.substr(3, 2), 16);
  const b = parseInt(hex.substr(5, 2), 16);
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 180;
}

function getParentStatusOptions() {
  try {
    const raw = localStorage.getItem('leadWebFormData');
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    const itemsObj = parsed?.webForm?.leads_form_items || {};
    const items = Array.isArray(itemsObj) ? itemsObj : Object.values(itemsObj);

    return items
      .filter((f: any) => f?.statusOption === 'is_status')
      .map((f: any) => ({
        value: f?.key_name || '',
        label: f?.alias_name || f?.key_name || '',
      }))
      .filter((opt: any) => opt.value);
  } catch {
    return [];
  }
}

const LEAD_STATUS_SENTINEL = '__LEAD_STATUS__';

const StatusForm: React.FC<StatusFormProps> = ({
  status,
  onChange,
  onRemove,
  sNo,
  validationError,
}) => {
  const { statusId } = useParams();

  const updateField = (key: keyof Status | 'type_of_status', value: any) => {
    onChange({ ...(status as any), [key]: value } as Status);
  };

  const [colorError, setColorError] = useState<Record<string, boolean>>({});
  const parentOptions = useMemo(() => getParentStatusOptions(), []);
  const typeOptions = useMemo(
    () => [{ value: LEAD_STATUS_SENTINEL, label: 'Lead Status' }, ...parentOptions],
    [parentOptions]
  );

  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    if ((status as any)?.type_of_status) return;

    if (parentOptions.length > 0) {
      updateField('type_of_status', parentOptions[0].value);
    } else {
      if (!status?.status_name) updateField('status_name', '');
    }
  }, []);

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    if (!isLightColor(newColor)) {
      updateField('colorCode' as any, newColor);
      setColorError((prev) => ({ ...prev, [sNo]: true }));
    } else {
      updateField('colorCode' as any, newColor);
      setColorError((prev) => ({ ...prev, [sNo]: false }));
    }
  };

  const updateTrigger = (index: number, trigger: Trigger) => {
    const newTriggers = [...(status.triggers || [])];
    newTriggers[index] = trigger;
    onChange({ ...(status as any), triggers: newTriggers });
  };

  const addTrigger = () => {
    onChange({
      ...(status as any),
      triggers: [...(status.triggers || []), { trigger_type: 'sms', stakeholder_id: '' }],
    });
  };

  const removeTrigger = (index: number) => {
    const newTriggers = [...(status.triggers || [])];
    newTriggers.splice(index, 1);
    onChange({ ...(status as any), triggers: newTriggers });
  };

  const typeOfStatusValue = (status as any)?.type_of_status || LEAD_STATUS_SENTINEL;

  const getTypeOfSubStatusList = () => {
    try {
      const raw = localStorage.getItem('leadWebFormData');
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      const itemsObj = parsed?.webForm?.leads_form_items || {};
      const items = Array.isArray(itemsObj) ? itemsObj : Object.values(itemsObj);
      return items?.filter(
        (f: any) =>
          f?.statusOption === 'is_sub_status' && f?.status_parent_key === status?.type_of_status
      );
    } catch {
      return [];
    }
  };
  return (
    <Box sx={{ border: '1px solid #ccc', p: 2, mt: 2, position: 'relative', borderRadius: 1 }}>
      <Grid
        container
        spacing={1}
        sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
      >
        {/* Type of status (Parent key) FIRST */}
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <TextField
            select
            fullWidth
            size="small"
            label={
              <Typography component="span">
                Type of status (Parent key) <span style={{ color: 'red' }}>*</span>
              </Typography>
            }
            value={typeOfStatusValue}
            onChange={(e) => {
              const val = e.target.value;
              if (val === LEAD_STATUS_SENTINEL) {
                updateField('type_of_status', 'default');
              } else {
                updateField('type_of_status', val);
              }
            }}
            margin="dense"
            variant="outlined"
            sx={{ mb: 2 }}
            error={!!validationError?.type_of_status}
            helperText={validationError?.type_of_status}
          >
            {typeOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Status Name */}
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <TextField
            label={
              <Typography component="span">
                Status Name <span style={{ color: 'red' }}>*</span>
              </Typography>
            }
            fullWidth
            value={status?.status_name || ''}
            onChange={(e) => updateField('status_name', e.target.value)}
            // disabled={statusId}
            margin="dense"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            error={!!validationError?.status_name}
            helperText={validationError?.status_name}
          />
        </Grid>

        {/* Active */}
        <Grid item xs={12} sm={6} md={4} lg={1}>
          <FormControlLabel
            control={
              <Switch
                sx={{ ml: 1 }}
                checked={!!status?.active}
                onChange={(e) => updateField('active', e.target.checked)}
                name="Active"
              />
            }
            label="Active"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
            <InputLabel htmlFor="light-color-picker">Choose a Color</InputLabel>
            <Input
              id="light-color-picker"
              type="color"
              value={(status as any)?.colorCode || ''}
              onChange={(e) => {
                const selectedColor = e?.target?.value?.toLowerCase();
                if (
                  selectedColor === '#ffffff' ||
                  selectedColor === '#fafafa' ||
                  selectedColor === '#fbf9f9' ||
                  selectedColor === '#fffafa' ||
                  selectedColor === '#f5f5f5'
                  // isLightColor(selectedColor)
                ) {
                  // updateField('colorCode' as any, '#1976d2');
                  setColorError((prev) => ({ ...prev, [sNo]: false }));
                } else {
                  handleColorChange(e);
                }
              }}
              style={{ width: '80px', height: '40px', border: 'none', cursor: 'pointer' }}
            />
            {status?.colorCode && (
              <CommonBadges
                value={status?.colorCode || 'Selected Color'}
                variant="outlined"
                color="primary"
              />
            )}

            <IconButton
              onClick={() => {
                updateField('colorCode' as any, '');
                setColorError((prev) => ({ ...prev, [sNo]: false }));
              }}
              size="small"
              aria-label="clear color"
            >
              <CloseIcon />
            </IconButton>
          </Box>
          {colorError[sNo] && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Please select a lighter color.
            </Alert>
          )}
        </Grid>

        {/* Alias Name */}
        {/* <Grid item xs={12} sm={5}>
          <TextField
            label={
              <Typography component="span">
                Alias Name <span style={{ color: 'red' }}>*</span>
              </Typography>
            }
            margin="dense"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ mb: 2 }}
            value={status?.alias_name || ''}
            onChange={(e) => updateField('alias_name', e.target.value)}
            error={!!validationError?.alias_name}
            helperText={validationError?.alias_name}
          />
        </Grid> */}
        {/* Color & Lead Closed */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, px: 1, pt: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={!!(status as any)?.isLeadClosed}
                onChange={(e) => updateField('isLeadClosed' as any, e.target.checked)}
                color="primary"
              />
            }
            label="Lead Closed"
          />
        </Box>
      </Grid>

    <Box sx={{ border: '1px solid #ccc', borderRadius: 2, p: 2, mt: 2 }}>
  <Typography variant="h6" sx={{ mb: 1 }}>
    Trigger & Activity
  </Typography>

  {(status?.triggers || []).map((trigger, idx) => (
    <TriggerForm
      key={idx}
      trigger={trigger}
      onChange={(updated) => updateTrigger(idx, updated)}
      onRemove={() => removeTrigger(idx)}
      sNo={idx}
    />
  ))}

  <Button onClick={addTrigger} variant="outlined" size="small" sx={{ mt: 1 }}>
    Add Trigger
  </Button>
</Box>

      {/* Substatuses */}
      <SubstatusList
        substatuses={status?.substatuses || []}
        onChange={(subs) => onChange({ ...(status as any), substatuses: subs })}
        validationError={validationError}
        statusSerialNumber={sNo}
        typeOfSubStatusList={getTypeOfSubStatusList()}
      />
    </Box>
  );
};

export default StatusForm;
